import AuditCriteriaSet, {
  AuditCriteriaChanges,
  AuditCriteriaGenerationItem,
  AuditCriteriaGenerationStatus,
  AuditCriteriaReviewSummary,
  AuditCriteriaReviewSummaryItem,
  AuditCriteriaStatus,
  AuditCriterion,
  AuditCriterionOneTimeState,
  AuditProcedureRef,
  auditCriterionStateActive,
  auditCriterionStateArchived,
  EvidenceTaskAdvice,
  EvidenceTaskAdviceCheck,
  EvidenceTaskBinding,
  EvidenceTaskBindingRow,
  NormCriteriaStatsRow,
  normalizeNormIds,
} from 'models/auditCriteria';
import { AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import { AICriterionOutcome } from 'models/auditCriteriaGenericSets';
import { AICriterionOutcomeDTO } from 'models/dto/auditCriteriaGenericDTO';
import AuditCriteriaSetDTO, {
  AuditCriterionStateDTO,
  AuditCriteriaChangesDTO,
  AuditCriteriaEditDTO,
  AuditCriteriaGenerationStartDTO,
  AuditCriteriaGenerationStatusDTO,
  AuditCriteriaItemKeyDTO,
  AuditCriteriaReviewSummaryDTO,
  AuditCriteriaReviewSummaryItemDTO,
  AuditCriteriaStatusDTO,
  AuditCriteriaStatusQueryDTO,
  AuditCriterionDTO,
  AuditCriterionEvidenceTaskDTO,
  AuditProcedureRefDTO,
  CreateEvidenceTaskRequestDTO,
  EvidenceTaskAdviceCheckDTO,
  EvidenceTaskAdviceDTO,
  EvidenceTaskBindEditDTO,
  EvidenceTaskBindingRowDTO,
  NormCriteriaStatsRowDTO,
} from 'models/dto/auditCriteriaDTO';

export function mapFromAuditCriteriaSetDTO(dto: AuditCriteriaSetDTO): AuditCriteriaSet {
  const output = new AuditCriteriaSet();
  output.entityType = dto.entityType;
  output.entityId = dto.entityId;
  output.isLeaf = dto.isLeaf ?? true;
  output.hasCriteria = dto.hasCriteria;
  output.signature = dto.signature ?? '';
  output.outOfSync = dto.outOfSync;
  output.generatedOn = dto.generatedOn ? new Date(dto.generatedOn) : undefined;
  output.modifiedOn = dto.modifiedOn ? new Date(dto.modifiedOn) : undefined;
  output.modifiedById = dto.modifiedById ?? undefined;
  output.reviewedOn = dto.reviewedOn ? new Date(dto.reviewedOn) : undefined;
  output.reviewedById = dto.reviewedById ?? undefined;
  output.reviewRequiredOn = dto.reviewRequiredOn ? new Date(dto.reviewRequiredOn) : undefined;
  output.reviewRequiredReason = dto.reviewRequiredReason ?? undefined;
  output.linkedNormIds = normalizeNormIds(dto.linkedNormIds ?? undefined);
  output.reevaluatedOn = dto.reevaluatedOn ? new Date(dto.reevaluatedOn) : undefined;
  output.reevaluatedReason = dto.reevaluatedReason ?? undefined;
  output.reevaluating = dto.reevaluating ?? false;
  output.changes = mapFromAuditCriteriaChangesDTO(dto.changes ?? undefined);
  output.criteria = (dto.criteria ?? []).map(mapFromAuditCriterionDTO);
  output.layer = dto.layer ?? AuditCriteriaLayer.None;
  output.auditCriteriaSetId = dto.auditCriteriaSetId ?? undefined;
  output.subType = dto.subType ?? undefined;
  output.sourceSetId = dto.sourceSetId ?? undefined;
  output.copiedOn = dto.copiedOn ? new Date(dto.copiedOn) : undefined;
  output.sourceChangedOn = dto.sourceChangedOn ? new Date(dto.sourceChangedOn) : undefined;
  output.familyId = dto.familyId ?? undefined;
  output.headTaskTypeId = dto.headTaskTypeId ?? undefined;
  output.headVersion = dto.headVersion ?? undefined;
  output.latestOutcomes = (dto.latestOutcomes ?? []).map(mapFromAICriterionOutcomeDTO);

  return output;
}

export function mapFromAICriterionOutcomeDTO(dto: AICriterionOutcomeDTO): AICriterionOutcome {
  const output = new AICriterionOutcome();
  output.aiCriterionOutcomeId = dto.aiCriterionOutcomeId;
  output.entityType = dto.entityType;
  output.entityId = dto.entityId;
  output.lineageKey = dto.lineageKey;
  output.auditCriteriaSetId = dto.auditCriteriaSetId ?? undefined;
  output.sourceKind = dto.sourceKind;
  output.sourceRef = dto.sourceRef ?? '';
  output.sourceName = dto.sourceName ?? '';
  output.proposedResult = dto.proposedResult;
  output.summary = dto.summary ?? '';
  output.evidenceLinkIds = [...(dto.evidenceLinkIds ?? [])];
  output.status = dto.status;
  output.appliedToTaskId = dto.appliedToTaskId ?? undefined;
  output.created = dto.created ? new Date(dto.created) : undefined;
  output.appliedOn = dto.appliedOn ? new Date(dto.appliedOn) : undefined;

  return output;
}

// the one-time state the API sends; a value this client does not know reads as none, so the badge keeps its old colours
const toOneTimeState = (value: string | null | undefined): AuditCriterionOneTimeState | undefined =>
  value === 'Verified' || value === 'Pending' || value === 'Missing' ? value : undefined;

function mapFromAuditCriteriaChangesDTO(dto: AuditCriteriaChangesDTO | undefined): AuditCriteriaChanges {
  const output = new AuditCriteriaChanges();
  output.added = dto?.added ?? 0;
  output.reworded = dto?.reworded ?? 0;
  output.archived = dto?.archived ?? 0;

  return output;
}

export function mapToAuditCriteriaEditDTO(criteria: AuditCriterion[]): AuditCriteriaEditDTO {
  const dto = new AuditCriteriaEditDTO();
  dto.criteria = criteria.map(mapToAuditCriterionDTO);

  return dto;
}

export function mapFromAuditCriterionDTO(dto: AuditCriterionDTO): AuditCriterion {
  const output = new AuditCriterion();
  output.criterionKey = dto.criterionKey ?? '';
  output.lineageKey = dto.lineageKey ?? '';
  output.requirement = dto.requirement ?? '';
  output.successCriteria = dto.successCriteria ?? '';
  output.required = dto.required;
  output.mode = dto.mode ?? '';
  output.verification = dto.verification ?? 'AI';
  output.sourceLinkId = dto.sourceLinkId ?? undefined;
  output.sourceName = dto.sourceName ?? '';
  output.sourceKind = dto.sourceKind ?? '';
  output.sourceText = dto.sourceText ?? '';
  output.conflictNote = dto.conflictNote ?? '';
  output.generatedOn = dto.generatedOn ? new Date(dto.generatedOn) : undefined;
  output.archivedOn = dto.archivedOn ? new Date(dto.archivedOn) : undefined;
  output.archivedById = dto.archivedById ?? undefined;
  output.requiresComment = dto.requiresComment ?? false;
  output.requiresEvidence = dto.requiresEvidence ?? false;
  // the API writes null for "no rows": that is the unrestricted (legacy) criterion, kept as undefined
  // so a whole-set save sends it back as null and never converts it
  output.normIds = dto.normIds && dto.normIds.length > 0 ? normalizeNormIds(dto.normIds) : undefined;
  output.effectiveNormIds = normalizeNormIds(dto.effectiveNormIds ?? undefined);
  output.evidenceTasks = (dto.evidenceTasks ?? []).map(mapFromAuditCriterionEvidenceTaskDTO);
  output.changeKind = dto.changeKind ?? undefined;
  output.oneTimeState = toOneTimeState(dto.oneTimeState);
  output.auditCriterionId = dto.auditCriterionId ?? 0;
  // a server without the state field still says archived through the stamp
  output.state = dto.state ?? (output.archivedOn ? auditCriterionStateArchived : auditCriterionStateActive);
  output.externalId = dto.externalId ?? '';
  output.columnName = dto.columnName ?? '';
  output.commentTrailId = dto.commentTrailId ?? 0;
  output.auditTrailId = dto.auditTrailId ?? 0;
  output.created = dto.created ? new Date(dto.created) : undefined;
  output.createdById = dto.createdById ?? undefined;
  output.modified = dto.modified ? new Date(dto.modified) : undefined;
  output.modifiedById = dto.modifiedById ?? undefined;

  return output;
}

function mapToAuditCriterionDTO(criterion: AuditCriterion): AuditCriterionDTO {
  const dto = new AuditCriterionDTO();
  dto.criterionKey = criterion.criterionKey;
  dto.requirement = criterion.requirement;
  dto.successCriteria = criterion.successCriteria;
  dto.required = criterion.required;
  dto.mode = criterion.mode;
  dto.verification = criterion.verification;
  dto.sourceLinkId = criterion.sourceLinkId ?? null;
  dto.sourceKind = criterion.sourceKind || null;
  dto.sourceText = criterion.sourceText;
  dto.conflictNote = criterion.conflictNote;
  dto.requiresComment = criterion.requiresComment;
  dto.requiresEvidence = criterion.requiresEvidence;
  // null = "keep what is stored": the whole-set PUTs of the procedure form editor must never convert
  // a legacy criterion the user did not touch; the criterion editor sets a list when the user did
  dto.normIds = criterion.normIds ?? null;
  dto.externalId = criterion.externalId.trim() || null;
  // the criterion carries the stored value, so it is always sent; '' clears it (the server stores blank as null)
  dto.columnName = criterion.columnName.trim();
  // lineageKey, effectiveNormIds, changeKind, the state, the trails and the stamps are server-owned, so
  // they stay out of the write the same way sourceName does.

  return dto;
}

function mapFromAuditCriterionEvidenceTaskDTO(dto: AuditCriterionEvidenceTaskDTO): EvidenceTaskBinding {
  const output = new EvidenceTaskBinding();
  output.taskId = dto.taskId ?? 0;
  output.taskName = dto.taskName ?? '';
  output.mode = dto.mode ?? '';
  output.recurring = dto.recurring ?? false;
  output.taskClosed = dto.taskClosed ?? false;
  output.hidden = dto.hidden ?? false;
  output.criterionCount = dto.criterionCount ?? 1;
  output.aiAdviceStatus = dto.aiAdviceStatus ?? undefined;
  output.aiAdviceOverallStatus = dto.aiAdviceOverallStatus ?? undefined;
  output.aiAdviceOn = dto.aiAdviceOn ? new Date(dto.aiAdviceOn) : undefined;

  return output;
}

export function mapFromEvidenceTaskAdviceDTO(dto: EvidenceTaskAdviceDTO): EvidenceTaskAdvice {
  const output = new EvidenceTaskAdvice();
  output.taskId = dto.taskId ?? 0;
  output.taskName = dto.taskName ?? '';
  output.generatedOn = dto.generatedOn ? new Date(dto.generatedOn) : undefined;
  output.overallStatus = dto.overallStatus ?? '';
  output.markdown = dto.markdown ?? '';
  output.checks = (dto.checks ?? []).map(mapFromEvidenceTaskAdviceCheckDTO);

  return output;
}

function mapFromEvidenceTaskAdviceCheckDTO(dto: EvidenceTaskAdviceCheckDTO): EvidenceTaskAdviceCheck {
  const output = new EvidenceTaskAdviceCheck();
  output.criterionKey = dto.criterionKey ?? '';
  output.requirement = dto.requirement ?? '';
  output.status = dto.status ?? '';
  output.summary = dto.summary ?? '';

  return output;
}

export function mapFromEvidenceTaskBindingRowDTO(dto: EvidenceTaskBindingRowDTO): EvidenceTaskBindingRow {
  const output = new EvidenceTaskBindingRow();
  output.entityType = dto.entityType ?? 0;
  output.entityId = dto.entityId ?? 0;
  output.criterionKey = dto.criterionKey ?? '';
  output.taskId = dto.taskId ?? 0;
  output.taskName = dto.taskName ?? '';
  output.mode = dto.mode ?? '';
  output.hidden = dto.hidden ?? false;
  output.criterionCount = dto.criterionCount ?? 1;

  return output;
}

export function mapFromNormCriteriaStatsRowDTO(dto: NormCriteriaStatsRowDTO): NormCriteriaStatsRow {
  const output = new NormCriteriaStatsRow();
  output.controlId = dto.controlId ?? 0;
  output.isLeaf = dto.isLeaf ?? true;
  output.hasCriteria = dto.hasCriteria ?? false;
  output.criteriaCount = dto.criteriaCount ?? 0;
  output.oneTimeCriteriaCount = dto.oneTimeCriteriaCount ?? 0;
  output.reviewed = dto.reviewed ?? false;
  output.taskCount = dto.taskCount ?? 0;
  output.boundOneTimeCriteriaCount = dto.boundOneTimeCriteriaCount ?? 0;
  output.verifiedOneTimeCriteriaCount = dto.verifiedOneTimeCriteriaCount ?? 0;
  output.overruledTaskCount = dto.overruledTaskCount ?? 0;
  output.procedureCount = dto.procedureCount ?? 0;
  output.reviewRequiredOn = dto.reviewRequiredOn ? new Date(dto.reviewRequiredOn) : undefined;
  output.reviewRequiredReason = dto.reviewRequiredReason ?? undefined;
  output.needsReview = dto.needsReview ?? false;
  output.reevaluatedOn = dto.reevaluatedOn ? new Date(dto.reevaluatedOn) : undefined;
  output.changesSinceReview = dto.changesSinceReview ?? 0;

  return output;
}

export function mapFromAuditCriteriaGenerationStatusDTO(dto: AuditCriteriaGenerationStatusDTO): AuditCriteriaGenerationStatus {
  const output = new AuditCriteriaGenerationStatus();
  output.normId = dto.normId ?? 0;
  output.runStatus = dto.runStatus ?? 0;
  output.total = dto.total ?? 0;
  output.ready = dto.ready ?? 0;
  output.generated = dto.generated ?? 0;
  output.failed = dto.failed ?? 0;
  output.remaining = dto.remaining ?? 0;
  output.queued = dto.queued ?? 0;
  output.generic = dto.generic ?? 0;
  output.items = (dto.items ?? []).map((item) => {
    const row = new AuditCriteriaGenerationItem();
    row.entityType = item.entityType ?? 0;
    row.entityId = item.entityId ?? 0;
    row.code = item.code ?? undefined;
    row.name = item.name ?? undefined;
    row.status = item.status ?? 0;
    row.criteriaCount = item.criteriaCount ?? undefined;
    row.generated = item.generated ?? undefined;
    row.generic = item.generic ?? undefined;
    row.error = item.error ?? undefined;

    return row;
  });

  return output;
}

export function mapToAuditCriteriaGenerationStartDTO(
  items: { entityType: number; entityId: number }[] | undefined,
): AuditCriteriaGenerationStartDTO {
  const output = new AuditCriteriaGenerationStartDTO();
  output.items = (items ?? []).map((item) => {
    const key = new AuditCriteriaItemKeyDTO();
    key.entityType = item.entityType;
    key.entityId = item.entityId;

    return key;
  });

  return output;
}

export function mapToCreateEvidenceTaskRequestDTO(criterionKeys: string[], title: string): CreateEvidenceTaskRequestDTO {
  const output = new CreateEvidenceTaskRequestDTO();
  output.criterionKeys = [...criterionKeys];
  output.title = title;
  output.description = '';

  return output;
}

export function mapFromAuditCriteriaReviewSummaryDTO(dto: AuditCriteriaReviewSummaryDTO): AuditCriteriaReviewSummary {
  const output = new AuditCriteriaReviewSummary();
  output.count = dto.count ?? 0;
  output.items = (dto.items ?? []).map(mapFromAuditCriteriaReviewSummaryItemDTO);

  return output;
}

function mapFromAuditCriteriaReviewSummaryItemDTO(dto: AuditCriteriaReviewSummaryItemDTO): AuditCriteriaReviewSummaryItem {
  const output = new AuditCriteriaReviewSummaryItem();
  output.entityType = dto.entityType ?? 0;
  output.entityId = dto.entityId ?? 0;
  output.code = dto.code ?? '';
  output.name = dto.name ?? '';
  output.reviewRequiredOn = dto.reviewRequiredOn ? new Date(dto.reviewRequiredOn) : undefined;
  output.reviewRequiredReason = dto.reviewRequiredReason ?? undefined;

  return output;
}

export function mapToAuditCriterionStateDTO(criterionKeys: string[], state: string): AuditCriterionStateDTO {
  const dto = new AuditCriterionStateDTO();
  dto.criterionKeys = criterionKeys;
  dto.state = state;

  return dto;
}

export function mapToEvidenceTaskBindEditDTO(criterionKeys: string[]): EvidenceTaskBindEditDTO {
  const dto = new EvidenceTaskBindEditDTO();
  dto.criterionKeys = criterionKeys;

  return dto;
}

export function mapToAuditCriteriaStatusQueryDTO(
  items: { entityType: number; entityId: number }[],
): AuditCriteriaStatusQueryDTO {
  const dto = new AuditCriteriaStatusQueryDTO();
  dto.items = items.map((item) => {
    const key = new AuditCriteriaItemKeyDTO();
    key.entityType = item.entityType;
    key.entityId = item.entityId;

    return key;
  });

  return dto;
}

export function mapFromAuditCriteriaStatusDTO(dto: AuditCriteriaStatusDTO): AuditCriteriaStatus {
  const output = new AuditCriteriaStatus();
  output.entityType = dto.entityType ?? 0;
  output.entityId = dto.entityId ?? 0;
  output.isLeaf = dto.isLeaf ?? true;
  output.hasCriteria = dto.hasCriteria ?? false;
  output.reviewed = dto.reviewed ?? false;
  output.layer = dto.layer ?? AuditCriteriaLayer.None;

  return output;
}

export function mapFromAuditProcedureRefDTO(dto: AuditProcedureRefDTO): AuditProcedureRef {
  const output = new AuditProcedureRef();
  output.taskId = dto.taskId ?? 0;
  output.name = dto.name ?? '';

  return output;
}
